import { Store } from 'flummox';
import Immutable from 'immutable';
import AudioFile from '../models/AudioFile';
import AudioPlaylist from '../models/AudioPlaylist';
import AudioPosition from '../models/AudioPosition';
import playlists from '../playlists';

function timeOfToday() {
  const today = new Date();
  today.setHours(0);
  today.setMinutes(0);
  today.setSeconds(0);

  return today;
}

function secondsInToday() {
  const now = new Date();
  const today = timeOfToday();

  return (now.getTime() - today.getTime()) / 1000;
}

function getPlaylistStartTime(playlist) {
  const duration = getPlaylistDuration(playlist);
  const now = secondsInToday();

  return now % duration;
}

function getPlaylistPositionForCurrentTime(playlist) {
  const startTime = getPlaylistStartTime(playlist);
  const position = getPlaylistPlayhead(playlist, startTime);

  return position;
}

function getPlaylistDuration(playlist) {
  if (!playlist || playlist.songs.size < 1) { return 0; }

  return playlist.songs.reduce((a, b) => {
    const dur = a.duration || a;
    return dur + b.duration;
  });
}

function getPlaylistTimePlayed(playlist, position) {
  if (!playlist || !position || playlist.songs.size < 1) { return 0; }
  let currentTime = 0;

  playlist.songs.forEach((song, i) => {
    if (i === position.index) {
      currentTime += position.time;
      return false;
    } else {
      currentTime += song.duration;
    }
  });

  return currentTime;
}

function getPlaylistPlayhead(playlist, time) {
  if (!playlist || playlist.songs.size < 1 || time > getPlaylistDuration(playlist)) { return 0; }
  let currentTime = 0;
  let result = null;

  playlist.songs.forEach((song, i) => {
    currentTime += song.duration;

    if (currentTime > time) {
      result = new AudioPosition({
        index: i,
        time: song.duration - (currentTime - time)
      });

      return false;
    }
  });

  return result;
}

const StateRecord = Immutable.Record({
  playlist: new AudioPlaylist(),
  playlists: Immutable.Map(),
  position: new AudioPosition(),
  loaded: false,
  loading: false,
  error: null,
  isPlaying: false,

  getCurrentSong: function currentSong() {
    if (this.playlist.songs.size < 1) { return null; }

    return this.playlist.songs.get(this.position.index);
  },

  getDuration: function duration() {
    return getPlaylistDuration(this.playlist);
  },

  getTotalTimePlayed: function totalTimePlayed() {
    return getPlaylistTimePlayed(this.playlist, this.position);
  },

  getPlayhead: function playhead(time) {
    return getPlaylistPlayhead(this.playlist, time);
  },

  getPositionForCurrentTime: function getPositionForCurrentTime() {
    return getPlaylistPositionForCurrentTime(this.playlist);
  }
});

export default class PlaylistStore extends Store {
  constructor(flux) {
    super(flux);

    const messageActionIds = flux.getActionIds('playlist');
    this.register(messageActionIds.play, this.play);
    this.register(messageActionIds.stop, this.stop);
    this.register(messageActionIds.next, this.next);
    this.register(messageActionIds.updateCurrentTime, this.updateCurrentTime);
    this.registerAsync(messageActionIds.load, this.loadBegin, this.loadSuccess, this.loadFail);

    this.state = {
      immutable: new StateRecord()
    };
  }

  updateCurrentTime(time) {
    if (!this.state.immutable.isPlaying) { return; }

    this.setState({
      immutable: this.state.immutable
        .set('position', new AudioPosition({
          index: this.state.immutable.position.index,
          time: time
        }))
    });
  }

  next() {
    if (!this.state.immutable.isPlaying) {
      this.play();
      return;
    }

    let next = this.state.immutable.position.index + 1;

    if (next >= this.state.immutable.playlist.songs.size) {
      next = 0;
    }

    this.setState({
      immutable: this.state.immutable
        .set('position', new AudioPosition({
          index: next,
          time: 0
        }))
    });
  }

  randomPlaylist() {
    const keys = Object.keys(playlists);
    const index = Math.floor(Math.random() * keys.length);
    const key = keys[index];

    return playlists[key];
  }

  injectDropsIntoPlaylist(playlist) {
    const totalDuration = getPlaylistDuration(playlist);
    const currentTime = getPlaylistPositionForCurrentTime(playlist);
    const songs = playlist.songs.toArray();
    const firstIndex = currentTime.index + 1;
    const totalLength = playlist.songs.size;
    const dropDuration = totalDuration / 12;
    let indexes = [firstIndex];
    let currentDuration = 0;


    for (var i = firstIndex; i < totalLength; i++) {
      let song = songs[i];
      currentDuration += song.duration;

      if (currentDuration >= dropDuration) {
        indexes.push(i + indexes.length);
        currentDuration = 0;
      }
    }

    for (var j = 0; j < firstIndex; j++) {
      let song = songs[j];
      currentDuration += song.duration;

      if (currentDuration >= dropDuration) {
        indexes.push(j + indexes.length);
        currentDuration = 0;
      }
    }

    for (var k = 0, l = indexes.length; k < l; k++) {
      let index = indexes[k];
      let dropIndex = k % playlist.drops.size;
      songs.splice(index, 0, playlist.drops.get(dropIndex));
    }

    playlist = playlist.set('songs', Immutable.List(songs));

    return playlist;
  }

  play(playlistKey) {
    let playlist = null;
    const currentPlaylist = !this.state.immutable.playlist.isEmpty() ? this.state.immutable.playlist : null;

    if (currentPlaylist && !playlistKey) {
      playlist = currentPlaylist;
    } else {
      playlist = !playlistKey ? this.randomPlaylist() : playlists[playlistKey];
    }

    playlist = this.injectDropsIntoPlaylist(playlist);

    let newState = this.state.immutable
      .set('playlist', playlist);

    const position = newState.getPositionForCurrentTime();

    newState = newState
      .set('position', position)
      .set('isPlaying', true);

    this.setState({
      immutable: newState
    });
  }

  stop() {
    if (!this.state.immutable.isPlaying) { return; }

    this.setState({
      immutable: this.state.immutable
        .set('isPlaying', false)
    });
  }

  loadBegin() {
    this.setState({
      immutable: this.state.immutable
        .set('loading', true)
        .set('loaded', false)
        .set('isPlaying', false)
        .set('position', new AudioPosition())
        .set('playlists', Immutable.Map())
    });
  }

  loadSuccess(result) {
    let loadedPlaylists = Immutable.Map();

    if (result) {
      loadedPlaylists = Immutable.Map(result);
    }

    this.setState({
      immutable: this.state.immutable
        .set('loaded', true)
        .set('loading', false)
        .set('error', null)
        .set('playlists', loadedPlaylists)
    });
  }

  loadFail(err) {
    this.setState({
      immutable: this.state.immutable
        .set('loading', false)
        .set('loaded', false)
        .set('error', err)
    });
  }
}


/** WEBPACK FOOTER **
 ** ./src/stores/PlaylistStore.es6
 **/